import React from 'react'
import { Sparkles, User, Building2, DollarSign, Calendar } from 'lucide-react'

export interface SaleDocumentExtracted {
  exchangerName?: string
  buyerName?: string
  closingCompanyName?: string
  closingCompanyAddress?: string
  closerName?: string
  purchaseAgreementDate?: string
  closingDate?: string
  purchasePrice?: number
  propertyAddress?: string
}

interface SaleNeelaRevealProps {
  extracted: SaleDocumentExtracted
}

function formatUSD(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export const SaleNeelaReveal: React.FC<SaleNeelaRevealProps> = ({ extracted }) => {
  return (
    <div
      className="rounded-xl border-2 border-emerald-300 dark:border-emerald-600 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 p-5"
      role="region"
      aria-label="Sales document extraction confirmed"
      data-testid="sale-neela-reveal"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800/40 flex items-center justify-center">
          <Sparkles
            className="h-5 w-5 text-emerald-600 dark:text-emerald-400"
            aria-hidden="true"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-0.5">
            Neela extracted your Sales &amp; Purchase Agreement
          </p>
          <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-4">
            Exchanger, property, and closing details are now pre-filled. Review each tab and confirm.
          </p>
          <div className="space-y-3">
            {(extracted.exchangerName || extracted.buyerName) && (
              <div className="rounded-lg bg-white/70 dark:bg-slate-800/50 border border-emerald-200 dark:border-emerald-700 px-4 py-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <User
                    className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                    aria-hidden="true"
                  />
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    Parties
                  </p>
                </div>
                {extracted.exchangerName && (
                  <p
                    className="text-sm font-medium text-emerald-800 dark:text-emerald-200"
                    data-testid="reveal-exchanger-name"
                  >
                    Exchanger: {extracted.exchangerName}
                  </p>
                )}
                {extracted.buyerName && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    Buyer: {extracted.buyerName}
                  </p>
                )}
              </div>
            )}

            {extracted.propertyAddress && (
              <div className="rounded-lg bg-white/70 dark:bg-slate-800/50 border border-emerald-200 dark:border-emerald-700 px-4 py-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Building2
                    className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                    aria-hidden="true"
                  />
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    Relinquished Property
                  </p>
                </div>
                <p
                  className="text-sm font-medium text-emerald-800 dark:text-emerald-200"
                  data-testid="reveal-property-address"
                >
                  {extracted.propertyAddress}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {extracted.purchasePrice != null && (
                <div className="rounded-lg bg-white/70 dark:bg-slate-800/50 border border-emerald-200 dark:border-emerald-700 px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <DollarSign
                      className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                      aria-hidden="true"
                    />
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                      Sale Price
                    </p>
                  </div>
                  <p
                    className="text-sm font-bold text-emerald-800 dark:text-emerald-200 tabular-nums"
                    data-testid="reveal-sale-price"
                  >
                    {formatUSD(extracted.purchasePrice)}
                  </p>
                </div>
              )}

              {(extracted.purchaseAgreementDate || extracted.closingDate) && (
                <div className="rounded-lg bg-white/70 dark:bg-slate-800/50 border border-emerald-200 dark:border-emerald-700 px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Calendar
                      className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                      aria-hidden="true"
                    />
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                      Dates
                    </p>
                  </div>
                  {extracted.purchaseAgreementDate && (
                    <p className="text-xs text-emerald-800 dark:text-emerald-200">
                      <span className="font-medium">Agreement:</span>{' '}
                      {formatDate(extracted.purchaseAgreementDate)}
                    </p>
                  )}
                  {extracted.closingDate && (
                    <p className="text-xs text-emerald-800 dark:text-emerald-200 mt-0.5">
                      <span className="font-medium">Closing:</span>{' '}
                      {formatDate(extracted.closingDate)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {extracted.closingCompanyName && (
              <div className="rounded-lg bg-white/70 dark:bg-slate-800/50 border border-emerald-200 dark:border-emerald-700 px-4 py-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Building2
                    className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                    aria-hidden="true"
                  />
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    Closing / Title Company
                  </p>
                </div>
                <p
                  className="text-sm font-medium text-emerald-800 dark:text-emerald-200"
                  data-testid="reveal-closing-company"
                >
                  {extracted.closingCompanyName}
                </p>
                {extracted.closingCompanyAddress && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {extracted.closingCompanyAddress}
                  </p>
                )}
                {extracted.closerName && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    Closer: {extracted.closerName}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
